import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Download, Trash2, Landmark, ArrowLeft } from "lucide-react";

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";

export const DocStatusBadge = ({ status }) => {
  const map = {
    pending_review: ["Pending review", "text-amber-700 bg-amber-50 border-amber-200"],
    approved: ["Approved", "text-emerald-700 bg-emerald-50 border-emerald-200"],
    changes_requested: ["Changes requested", "text-red-700 bg-red-50 border-red-200"],
  };
  const [label, cls] = map[status] || [status, "text-neutral-600 bg-neutral-50 border-neutral-200"];
  return <span data-testid={`doc-status-${status}`} className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>{label}</span>;
};

export const CATEGORY_LABELS = { for_review: "For Review", copyright: "Copyright", library_of_congress: "Library of Congress", other: "Other" };

export default function AuthorDocs() {
  const { id } = useParams();
  const [author, setAuthor] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loc, setLoc] = useState([]);
  const [form, setForm] = useState({ title: "", category: "for_review", file: null });
  const [locForm, setLocForm] = useState({ book_title: "", lccn: "", year: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/users").then((r) => setAuthor(r.data.find((u) => u.id === id)));
    api.get(`/admin/authors/${id}/documents`).then((r) => setDocs(r.data));
    api.get(`/admin/authors/${id}/loc`).then((r) => setLoc(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!form.title.trim() || !form.file) return toast.error("Title and file are required");
    const fd = new FormData();
    fd.append("file", form.file);
    fd.append("title", form.title);
    fd.append("category", form.category);
    setBusy(true);
    try {
      await api.post(`/admin/authors/${id}/documents`, fd);
      toast.success("Document uploaded — author notified");
      setForm({ title: "", category: "for_review", file: null });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const addLoc = async () => {
    if (!locForm.book_title.trim() || !locForm.lccn.trim()) return toast.error("Book title and LCCN are required");
    try {
      await api.post(`/admin/authors/${id}/loc`, locForm);
      toast.success("Library of Congress record added — author notified");
      setLocForm({ book_title: "", lccn: "", year: "", notes: "" });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deleteLoc = async (rid) => {
    if (!window.confirm("Delete this Library of Congress record?")) return;
    try {
      await api.delete(`/admin/loc/${rid}`);
      toast.success("Record deleted");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="author-docs-page">
      <Link to="/admin/users" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 mb-4"><ArrowLeft size={14} /> Back to users</Link>
      <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading mb-1">Documents — {author?.name || "..."}</h1>
      <p className="text-neutral-500 mb-8">{author?.email}</p>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h3 className="font-semibold font-heading mb-4 flex items-center gap-2"><Upload size={17} /> Upload a document</h3>
          <label className="text-sm font-medium text-neutral-700">Title</label>
          <input data-testid="doc-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="e.g. Publishing Agreement 2026" />
          <label className="text-sm font-medium text-neutral-700 mt-3 block">Category</label>
          <select data-testid="doc-category-select" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inputCls}>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="text-sm font-medium text-neutral-700 mt-3 block">File (.doc, .docx, .pdf, .png)</label>
          <input data-testid="doc-file-input" type="file" accept=".doc,.docx,.pdf,.png"
            onChange={(e) => setForm((f) => ({ ...f, file: e.target.files[0] || null }))}
            className="mt-1 w-full text-sm text-neutral-600 file:mr-3 file:px-3 file:py-1.5 file:border file:border-neutral-300 file:rounded-md file:bg-white file:text-sm file:font-medium hover:file:bg-neutral-50" />
          <button data-testid="doc-upload-button" onClick={upload} disabled={busy}
            className="mt-4 px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
            {busy ? "Uploading..." : "Upload & notify author"}
          </button>
        </div>

        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h3 className="font-semibold font-heading mb-4 flex items-center gap-2"><Landmark size={17} /> Add Library of Congress info</h3>
          <label className="text-sm font-medium text-neutral-700">Book title</label>
          <input data-testid="loc-book-input" value={locForm.book_title} onChange={(e) => setLocForm((f) => ({ ...f, book_title: e.target.value }))} className={inputCls} />
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div><label className="text-sm font-medium text-neutral-700">LCCN</label>
              <input data-testid="loc-lccn-input" value={locForm.lccn} onChange={(e) => setLocForm((f) => ({ ...f, lccn: e.target.value }))} className={inputCls} placeholder="2026001234" /></div>
            <div><label className="text-sm font-medium text-neutral-700">Year</label>
              <input value={locForm.year} onChange={(e) => setLocForm((f) => ({ ...f, year: e.target.value }))} className={inputCls} placeholder="2026" /></div>
          </div>
          <label className="text-sm font-medium text-neutral-700 mt-3 block">Notes</label>
          <input value={locForm.notes} onChange={(e) => setLocForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} />
          <button data-testid="loc-add-button" onClick={addLoc}
            className="mt-4 px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Add record & notify author</button>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden mb-8">
        <h3 className="font-semibold font-heading p-6 pb-2">Documents</h3>
        {docs.length === 0 ? <p className="p-6 pt-2 text-sm text-neutral-400">No documents uploaded yet.</p> : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-neutral-50">
              {["Title", "Category", "Status", "Author note", "Uploaded", ""].map((h, i) => (
                <th key={i} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {docs.map((d) => (
                <tr key={d.id} data-testid={`admin-doc-row-${d.id}`}>
                  <td className="px-6 py-3 font-medium text-neutral-900">{d.title}<p className="text-xs text-neutral-400 font-normal">{d.filename}</p></td>
                  <td className="px-6 py-3">{CATEGORY_LABELS[d.category]}</td>
                  <td className="px-6 py-3"><DocStatusBadge status={d.status} /></td>
                  <td className="px-6 py-3 text-neutral-500 max-w-xs truncate">{d.author_note || "—"}</td>
                  <td className="px-6 py-3">{new Date(d.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-3">
                    <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`, "_blank")}
                      className="p-1.5 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded-md"><Download size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <h3 className="font-semibold font-heading p-6 pb-2">Library of Congress records</h3>
        {loc.length === 0 ? <p className="p-6 pt-2 text-sm text-neutral-400">No records yet.</p> : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-neutral-50">
              {["Book", "LCCN", "Year", "Notes", "Added", ""].map((h, i) => (
                <th key={i} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {loc.map((r) => (
                <tr key={r.id} data-testid={`loc-row-${r.id}`}>
                  <td className="px-6 py-3 font-medium">{r.book_title}</td>
                  <td className="px-6 py-3 font-mono text-xs">{r.lccn}</td>
                  <td className="px-6 py-3">{r.year || "—"}</td>
                  <td className="px-6 py-3 text-neutral-500">{r.notes || "—"}</td>
                  <td className="px-6 py-3">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-3">
                    <button onClick={() => deleteLoc(r.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-md"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
